// src/components/inventory/ReturnItemModal.tsx
// ✅ FIXED 2024-12-22: Dynamic storage locations from tenant config

import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Assignment, ReturnFormData, RETURN_CONDITIONS, ReturnCondition } from '../../types/assignment';
import { inventoryAPI } from '../../services/inventoryAPI';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { logger } from '../../utils/logger';

const returnModalLogger = logger.createServiceLogger('ReturnItemModal');

interface ReturnItemModalProps {
  isOpen: boolean;
  assignment: Assignment | null;
  onClose: () => void;
  onReturn: (returnData: ReturnFormData) => Promise<void>;
}

export const ReturnItemModal: React.FC<ReturnItemModalProps> = ({
  isOpen,
  assignment,
  onClose,
  onReturn
}) => {
  const [condition, setCondition] = useState<string>(RETURN_CONDITIONS.GOOD);
  const [quantity, setQuantity] = useState(1);
  const [returnToLocation, setReturnToLocation] = useState('');
  const [returnedBy, setReturnedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [itemDetails, setItemDetails] = useState<any>(null);

  const isSerializedItem = !!assignment?.itemSerialNumber;

  useEffect(() => {
    if (isOpen && assignment) {
      resetForm();
      loadCurrentUser();
      loadAvailableLocations();
      loadItemDetails();
    }
  }, [isOpen, assignment]);

  const loadCurrentUser = async () => {
    try {
      const firstName = localStorage.getItem('userFirstName') || '';
      const lastName = localStorage.getItem('userLastName') || '';
      const userName = firstName || lastName 
        ? `${firstName} ${lastName}`.trim() 
        : localStorage.getItem('userName') || 'Current User';
      setReturnedBy(userName);
    } catch (err) {
      returnModalLogger.error('Error loading current user', undefined, err as Error);
      setReturnedBy('Current User');
    }
  };

  // ✅ FIXED: Load dynamic locations from tenant configuration
  const loadAvailableLocations = async () => {
    setLocationsLoading(true);
    try {
      returnModalLogger.debug('Loading storage locations from tenant config');
      
      const dynamicLocations = await tenantConfigAPI.getInventoryLocations();
      
      if (dynamicLocations && dynamicLocations.length > 0) {
        returnModalLogger.info('Loaded dynamic storage locations', { 
          count: dynamicLocations.length 
        });
        setAvailableLocations(dynamicLocations);
      } else {
        // Fallback: try to get locations from existing inventory items
        returnModalLogger.warn('No configured locations, falling back to inventory scan');
        const response = await inventoryAPI.getItems();
        if (response.success && response.data) {
          const locations = Array.from(
            new Set(response.data.map((item: any) => item.location).filter(Boolean))
          ).sort() as string[];
          
          if (locations.length > 0) {
            setAvailableLocations(locations);
            returnModalLogger.info('Loaded locations from inventory', { count: locations.length });
          } else {
            // Final fallback
            setAvailableLocations(['Main Storage']);
          }
        } else {
          setAvailableLocations(['Main Storage']);
        }
      }
    } catch (err) {
      returnModalLogger.error('Error loading locations', undefined, err as Error);
      setAvailableLocations(['Main Storage']);
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadItemDetails = async () => {
    if (!assignment) return;
    
    try {
      const response = await inventoryAPI.getItem(assignment.inventoryItemId);
      if (response.success && response.data) {
        setItemDetails(response.data);
        // Set default return location to item's original location
        if (response.data.location) {
          setReturnToLocation(response.data.location);
        }
      }
    } catch (err) {
      returnModalLogger.error('Error loading item details', undefined, err as Error);
    }
  };

  const resetForm = () => {
    setCondition(RETURN_CONDITIONS.GOOD);
    setQuantity(1);
    setReturnToLocation('');
    setNotes('');
    setError('');
    setItemDetails(null);
  };

  const validateForm = (): boolean => {
    if (!returnToLocation) {
      setError('Please select a return location');
      return false;
    }

    if (!isSerializedItem && (quantity < 1)) {
      setError('Quantity must be at least 1');
      return false;
    }

    setError('');
    return true;
  };

  const getConditionDescription = (cond: string): string => {
    switch (cond) {
      case 'Good':
        return 'Item is in good condition and ready for reissue';
      case 'Damaged':
        return 'Item needs repair or maintenance before reissue';
      case 'Lost':
        return 'Item is lost and cannot be returned';
      default:
        return '';
    }
  };

  const getConditionImpact = (cond: string): string => {
    switch (cond) {
      case 'Good':
        return 'Will be returned to available inventory';
      case 'Damaged':
        return 'Will be marked as "Needs Repair" in inventory';
      case 'Lost':
        return 'Will be removed from inventory permanently';
      default:
        return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const returnData: ReturnFormData = {
        condition: condition as ReturnCondition,
        notes: notes.trim() || '',
        ...((!isSerializedItem && quantity > 0) && { quantity }),
        ...(returnToLocation && { returnToLocation })
      };

      returnModalLogger.info('Submitting return', {
        assignmentId: assignment?.assignmentId,
        condition,
        returnToLocation,
        quantity: isSerializedItem ? 1 : quantity
      });

      await onReturn(returnData);
      onClose();
    } catch (err) {
      returnModalLogger.error('Error returning item', undefined, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to return item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !assignment) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Return Equipment</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Assignment Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="font-medium text-gray-900">{assignment.itemName}</div>
            {assignment.itemSerialNumber && (
              <div className="text-sm text-gray-600">Serial: {assignment.itemSerialNumber}</div>
            )}
            <div className="text-sm text-gray-600">Officer: {assignment.assignedToUserName} ({assignment.assignedToUserBadge})</div>
            <div className="text-sm text-gray-600">
              Assigned: {new Date(assignment.assignmentDate).toLocaleDateString()}
            </div>
            <div className="text-sm text-gray-600">
              Days Assigned: {Math.ceil((new Date().getTime() - new Date(assignment.assignmentDate).getTime()) / (1000 * 60 * 60 * 24))}
            </div>
            {itemDetails && (
              <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                <div>Type: {isSerializedItem ? 'Serialized' : 'Bulk'}</div>
                {itemDetails.category && <div>Category: {itemDetails.category}</div>}
                {itemDetails.location && <div>Original Location: {itemDetails.location}</div>}
              </div>
            )}
          </div>

          {/* Condition (for serialized items only) */}
          {isSerializedItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition on Return *
              </label>
              <div className="space-y-3">
                {Object.values(RETURN_CONDITIONS).map((conditionOption) => (
                  <div key={conditionOption} className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id={conditionOption}
                      name="condition"
                      value={conditionOption}
                      checked={condition === conditionOption}
                      onChange={(e) => setCondition(e.target.value)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={loading}
                    />
                    <label htmlFor={conditionOption} className="text-sm">
                      <div className="font-medium text-gray-900">{conditionOption}</div>
                      <div className="text-gray-600">{getConditionDescription(conditionOption)}</div>
                      <div className="text-blue-600 text-xs mt-1">{getConditionImpact(conditionOption)}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quantity (for bulk items only) */}
          {!isSerializedItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity Returned *
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    className="w-24 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-600">
                    (Returning partial quantity from bulk assignment)
                  </span>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-yellow-500 text-lg">⚠️</span>
                    <div className="text-sm text-yellow-800">
                      <strong>Important:</strong> You are returning <strong>{quantity}</strong> unit{quantity !== 1 ? 's' : ''} 
                      from this bulk assignment. The remaining items will still be assigned to this officer.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Return to Location - ✅ FIXED: Dynamic from tenant config */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return to Location *
            </label>
            <div className="relative">
              <select
                value={returnToLocation}
                onChange={(e) => setReturnToLocation(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                required
                disabled={loading || locationsLoading}
              >
                <option value="">
                  {locationsLoading ? 'Loading locations...' : 'Select location...'}
                </option>
                {availableLocations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
              {locationsLoading && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Equipment will be stored at this location after return
            </p>
            {availableLocations.length === 0 && !locationsLoading && (
              <p className="text-xs text-amber-600 mt-1">
                No storage locations configured. Please contact your administrator.
              </p>
            )}
          </div>

          {/* Returned By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Returned by
            </label>
            <input
              type="text"
              value={returnedBy}
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional notes about the condition, any issues, or return circumstances..."
              disabled={loading}
            />
          </div>

          {/* Return Process Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Return Process Summary</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <div>• Assignment will be marked as "Returned"</div>
              <div>• Equipment will be returned to: <strong>{returnToLocation || 'Selected Location'}</strong></div>
              {isSerializedItem && (
                <div>• Item condition: <strong>{condition}</strong> - {getConditionImpact(condition)}</div>
              )}
              {!isSerializedItem && (
                <div>• Quantity being returned: <strong>{quantity}</strong> unit{quantity !== 1 ? 's' : ''}</div>
              )}
              <div>• Assignment history will be preserved for audit trail</div>
              <div>• Officer will no longer have this equipment assigned</div>
            </div>
          </div>

          {/* Special Warning for Lost/Damaged Items */}
          {isSerializedItem && (condition === RETURN_CONDITIONS.LOST || condition === RETURN_CONDITIONS.DAMAGED) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <span className="text-yellow-500 text-lg">⚠️</span>
                <div className="text-sm text-yellow-800">
                  <strong>Important:</strong> 
                  {condition === RETURN_CONDITIONS.LOST 
                    ? ' Lost equipment will be permanently removed from inventory and may require replacement or disciplinary action according to department policy.'
                    : ' Damaged equipment will need to be inspected and repaired before it can be reissued to other officers.'
                  }
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || locationsLoading || !returnToLocation}
            >
              {loading ? 'Processing Return...' : `Return Equipment${!isSerializedItem ? ` (${quantity})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};